<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AutomationSelect from './AutomationSelect.vue';
import TagsInput from './TagsInput.vue';
import AutomationTaskDetailPanel from './AutomationTaskDetailPanel.vue';
import {
  statusesForCategory,
  statusLabel,
} from '../../constants/statusByCategory';

const props = defineProps({
  actions: { type: Array, default: () => [] },
  single: { type: Boolean, default: false },
  hideAddButton: { type: Boolean, default: false },
  pipelineId: { type: [Number, String], default: null },
});
const emit = defineEmits(['update:actions']);

const store = useStore();
const { t } = useI18n();

const expandedActionIndex = ref(null);

function openTaskDetail(index) {
  expandedActionIndex.value = index;
}
function closeTaskDetail() {
  expandedActionIndex.value = null;
}

const pipelines = computed(() => store.getters['kanban/pipelines'] ?? []);

const actionPipeline = computed(() =>
  pipelines.value.find(p => p.id === props.pipelineId)
);
const actionPipelineCategory = computed(
  () => actionPipeline.value?.category ?? 'sales'
);
const isPostSalesAction = computed(
  () => actionPipelineCategory.value === 'post_sales'
);
const agents = computed(() => store.getters['agents/getAgents'] ?? []);
const labels = computed(() => store.getters['labels/getLabels'] ?? []);

const actionTypeOptions = computed(() => [
  { value: 'create_task', label: t('KANBAN.AUTOMATIONS.ACTIONS.CREATE_TASK') },
  {
    value: 'apply_task_group',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.APPLY_TASK_GROUP'),
  },
  {
    value: 'send_message',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.SEND_MESSAGE'),
  },
  {
    value: 'trigger_flow',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.TRIGGER_FLOW'),
  },
  {
    value: 'trigger_webhook',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.TRIGGER_WEBHOOK'),
  },
  {
    value: 'change_assignee',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.CHANGE_ASSIGNEE'),
  },
  {
    value: 'add_watchers',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.ADD_WATCHERS'),
  },
  { value: 'add_tag', label: t('KANBAN.AUTOMATIONS.ACTIONS.ADD_TAG') },
  { value: 'remove_tag', label: t('KANBAN.AUTOMATIONS.ACTIONS.REMOVE_TAG') },
  {
    value: 'change_status',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.CHANGE_STATUS'),
  },
  { value: 'move_card', label: t('KANBAN.AUTOMATIONS.ACTIONS.MOVE_CARD') },
  { value: 'create_card', label: t('KANBAN.AUTOMATIONS.ACTIONS.CREATE_CARD') },
  {
    value: 'duplicate_card',
    label: t('KANBAN.AUTOMATIONS.ACTIONS.DUPLICATE_CARD'),
  },
]);
const statusOptions = computed(() =>
  statusesForCategory(actionPipelineCategory.value).map(status => ({
    value: status,
    label: statusLabel(actionPipelineCategory.value, status),
  }))
);
const pipelineOptions = computed(() =>
  pipelines.value.map(p => ({ value: p.id, label: p.name }))
);
const agentOptions = computed(() =>
  agents.value.map(a => ({ value: a.id, label: a.name }))
);
const labelOptions = computed(() =>
  labels.value.map(l => ({ value: l.title, label: l.title, color: l.color }))
);
const taskGroups = computed(
  () => store.getters['kanbanTaskGroups/getGroups'] ?? []
);
const taskGroupOptions = computed(() =>
  taskGroups.value.map(g => ({ value: g.id, label: g.name }))
);
const activeFlows = computed(() => store.getters['flows/getActiveFlows'] ?? []);
const flowOptions = computed(() =>
  activeFlows.value.map(f => ({ value: f.id, label: f.name }))
);
const lossReasonOptions = computed(() =>
  (actionPipeline.value?.loss_reasons ?? []).map(r => ({ value: r, label: r }))
);
const cancelReasonOptions = computed(() =>
  (actionPipeline.value?.cancel_reasons ?? []).map(r => ({
    value: r,
    label: r,
  }))
);
const waitingReasonOptions = computed(() =>
  (actionPipeline.value?.waiting_reasons ?? []).map(r => ({
    value: r,
    label: r,
  }))
);

const columnOptionsFor = pipelineId => {
  const pipeline = pipelines.value.find(p => p.id === pipelineId);
  return (pipeline?.columns ?? []).map(c => ({ value: c.id, label: c.name }));
};

function addAction() {
  emit('update:actions', [...props.actions, { type: 'add_tag', config: {} }]);
}

function changeType(index, type) {
  const next = props.actions.map((a, i) =>
    i === index ? { type, config: {} } : a
  );
  emit('update:actions', next);
}

function updateConfig(index, patch) {
  const next = props.actions.map((a, i) =>
    i === index ? { ...a, config: { ...a.config, ...patch } } : a
  );
  emit('update:actions', next);
}

function removeAction(index) {
  emit(
    'update:actions',
    props.actions.filter((_, i) => i !== index)
  );
}

// normaliza tags: aceita config.tags (array) ou o legado config.tag (string)
function tagsValue(config) {
  if (Array.isArray(config.tags)) return config.tags;
  if (config.tag) return [config.tag];
  return [];
}

function move(index, dir) {
  const target = index + dir;
  if (target < 0 || target >= props.actions.length) return;
  const next = [...props.actions];
  [next[index], next[target]] = [next[target], next[index]];
  emit('update:actions', next);
}

onMounted(() => {
  store.dispatch('kanbanTaskGroups/fetch');
  store.dispatch('flows/fetch');
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <span
      v-if="!single"
      class="text-xs font-semibold uppercase text-n-slate-10"
    >
      {{ $t('KANBAN.AUTOMATIONS.THEN') }}
    </span>

    <div
      v-for="(action, index) in actions"
      :key="index"
      class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-2.5"
    >
      <div class="flex items-center gap-2">
        <div v-if="!single" class="flex flex-col">
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-12 leading-none disabled:opacity-30"
            :disabled="index === 0"
            @click="move(index, -1)"
          >
            <span class="i-lucide-chevron-up size-3.5" />
          </button>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-12 leading-none disabled:opacity-30"
            :disabled="index === actions.length - 1"
            @click="move(index, 1)"
          >
            <span class="i-lucide-chevron-down size-3.5" />
          </button>
        </div>
        <div class="flex-1">
          <AutomationSelect
            :model-value="action.type"
            :options="actionTypeOptions"
            :placeholder="$t('KANBAN.AUTOMATIONS.THEN')"
            @update:model-value="changeType(index, $event)"
          />
        </div>
        <button
          v-if="!single"
          type="button"
          class="text-n-ruby-11 hover:text-n-ruby-10"
          :aria-label="$t('KANBAN.AUTOMATIONS.DELETE')"
          @click="removeAction(index)"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>

      <!-- move_card / create_card: pipeline + column -->
      <div
        v-if="['move_card', 'create_card'].includes(action.type)"
        class="flex items-center gap-2 flex-wrap"
      >
        <AutomationSelect
          compact
          :model-value="action.config.pipeline_id || null"
          :options="pipelineOptions"
          :placeholder="$t('KANBAN.AUTOMATIONS.SELECT_PIPELINE')"
          @update:model-value="
            updateConfig(index, { pipeline_id: $event, column_id: null })
          "
        />
        <AutomationSelect
          compact
          :model-value="action.config.column_id || null"
          :options="columnOptionsFor(action.config.pipeline_id)"
          :placeholder="$t('KANBAN.AUTOMATIONS.SELECT_COLUMN')"
          @update:model-value="updateConfig(index, { column_id: $event })"
        />
      </div>

      <!-- create_card title -->
      <input
        v-if="action.type === 'create_card'"
        type="text"
        :value="action.config.title || ''"
        :placeholder="$t('KANBAN.AUTOMATIONS.TITLE_PLACEHOLDER')"
        class="no-margin !h-8 rounded-lg border border-n-weak bg-n-background px-2.5 text-sm text-n-slate-12 focus:outline-none"
        @input="updateConfig(index, { title: $event.target.value })"
      />

      <!-- change_status -->
      <div
        v-if="action.type === 'change_status'"
        class="flex items-center gap-2 flex-wrap"
      >
        <AutomationSelect
          compact
          :model-value="action.config.status || 'open'"
          :options="statusOptions"
          placeholder="status"
          @update:model-value="updateConfig(index, { status: $event })"
        />
        <AutomationSelect
          v-if="action.config.status === 'lost' && !isPostSalesAction"
          :model-value="action.config.loss_reason || null"
          :options="lossReasonOptions"
          :placeholder="$t('KANBAN.AUTOMATIONS.LOSS_REASON_PLACEHOLDER')"
          @update:model-value="updateConfig(index, { loss_reason: $event })"
        />
        <AutomationSelect
          v-if="action.config.status === 'lost' && isPostSalesAction"
          :model-value="action.config.cancel_reason || null"
          :options="cancelReasonOptions"
          :placeholder="$t('KANBAN.AUTOMATIONS.CANCEL_REASON_PLACEHOLDER')"
          @update:model-value="updateConfig(index, { cancel_reason: $event })"
        />
        <AutomationSelect
          v-if="action.config.status === 'waiting'"
          :model-value="action.config.waiting_reason || null"
          :options="waitingReasonOptions"
          :placeholder="$t('KANBAN.AUTOMATIONS.WAITING_REASON_PLACEHOLDER')"
          @update:model-value="updateConfig(index, { waiting_reason: $event })"
        />
      </div>

      <!-- change_assignee -->
      <AutomationSelect
        v-if="action.type === 'change_assignee'"
        compact
        :model-value="action.config.assignee_id || null"
        :options="agentOptions"
        :placeholder="$t('KANBAN.AUTOMATIONS.SELECT_AGENT')"
        @update:model-value="updateConfig(index, { assignee_id: $event })"
      />

      <!-- add_watchers -->
      <AutomationSelect
        v-if="action.type === 'add_watchers'"
        compact
        multiple
        :model-value="action.config.user_ids || []"
        :options="agentOptions"
        :placeholder="$t('KANBAN.AUTOMATIONS.SELECT_AGENT')"
        @update:model-value="updateConfig(index, { user_ids: $event })"
      />

      <!-- create_task -->
      <div v-if="action.type === 'create_task'" class="flex items-center gap-2">
        <input
          type="text"
          :value="action.config.title || ''"
          :placeholder="$t('KANBAN.AUTOMATIONS.TITLE_PLACEHOLDER')"
          class="no-margin !h-8 flex-1 rounded-lg border border-n-weak bg-n-background px-2.5 text-sm text-n-slate-12 focus:outline-none"
          @input="updateConfig(index, { title: $event.target.value })"
        />
        <button
          type="button"
          class="size-8 flex-shrink-0 flex items-center justify-center rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          :aria-label="t('KANBAN.AUTOMATIONS.CREATE_TASK_OPEN_DETAIL')"
          @click="openTaskDetail(index)"
        >
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>

      <AutomationTaskDetailPanel
        v-if="action.type === 'create_task' && expandedActionIndex === index"
        :model-value="action.config"
        @update:model-value="updateConfig(index, $event)"
        @close="closeTaskDetail"
      />

      <!-- add_tag / remove_tag -->
      <TagsInput
        v-if="['add_tag', 'remove_tag'].includes(action.type)"
        :model-value="tagsValue(action.config)"
        :options="labelOptions"
        @update:model-value="updateConfig(index, { tags: $event })"
      />

      <!-- send_message -->
      <textarea
        v-if="action.type === 'send_message'"
        :value="action.config.content || ''"
        :placeholder="$t('KANBAN.AUTOMATIONS.MESSAGE_PLACEHOLDER')"
        rows="3"
        class="rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand resize-y"
        @input="updateConfig(index, { content: $event.target.value })"
      />

      <!-- trigger_webhook -->
      <input
        v-if="action.type === 'trigger_webhook'"
        type="text"
        :value="action.config.url || ''"
        :placeholder="$t('KANBAN.AUTOMATIONS.WEBHOOK_URL_PLACEHOLDER')"
        class="no-margin !h-8 rounded-lg border border-n-weak bg-n-background px-2.5 text-sm text-n-slate-12 focus:outline-none"
        @input="updateConfig(index, { url: $event.target.value })"
      />

      <!-- apply_task_group -->
      <AutomationSelect
        v-if="action.type === 'apply_task_group'"
        compact
        multiple
        :model-value="
          action.config.task_group_ids ||
          (action.config.task_group_id ? [action.config.task_group_id] : [])
        "
        :options="taskGroupOptions"
        :placeholder="$t('KANBAN.TASK_GROUPS.APPLY')"
        @update:model-value="updateConfig(index, { task_group_ids: $event })"
      />

      <!-- trigger_flow -->
      <AutomationSelect
        v-if="action.type === 'trigger_flow'"
        compact
        :model-value="action.config.flow_id || null"
        :options="flowOptions"
        :placeholder="$t('KANBAN.AUTOMATIONS.SELECT_FLOW')"
        @update:model-value="updateConfig(index, { flow_id: $event })"
      />

      <p v-if="action.type === 'trigger_flow'" class="text-xs text-n-slate-10">
        {{ $t('KANBAN.AUTOMATIONS.TRIGGER_FLOW_HINT') }}
      </p>

      <p
        v-if="
          ['create_card', 'create_task', 'send_message'].includes(action.type)
        "
        class="text-xs text-n-slate-10"
      >
        {{ $t('KANBAN.AUTOMATIONS.TEMPLATE_HINT') }}
      </p>
    </div>

    <button
      v-if="!hideAddButton && !single"
      type="button"
      class="self-start text-sm text-n-brand hover:opacity-80 font-medium"
      @click="addAction"
    >
      {{ $t('KANBAN.AUTOMATIONS.ADD_ACTION') }}
    </button>
  </div>
</template>
